// Device-group service — thin wrapper over the /realtime/groups API.
//
// Membership is stored on the DEVICE (each realtime record carries `group_ids`),
// not on the group object, so the group shape here is just { id, name, createdAt,
// company_id?, company_name? }. "All Devices" is virtual (ALL_DEVICES_ID) and never
// hits the API.
//
// Every call returns plain data and throws on transport errors; callers handle the
// `status: 'fail'` soft errors (duplicate name, group not found, …).
import {
    createRealtimeGroup,
    deleteRealtimeGroup,
    getRealtimeGroups,
    updateRealtimeGroup,
    updateRealtimeGroupDevices,
} from '../api/realtimeApi';

export const ALL_DEVICES_ID = 'all';

// Returns [{ id, name, createdAt, company_id?, company_name? }, ...] for the account.
// Throws when the API soft-fails (status: 'fail') so callers can surface the error
// instead of silently rendering an empty dropdown — the list endpoint is known to 500
// for vendor accounts, and a silent empty list hides that from the user.
export async function listGroups() {
    const data = await getRealtimeGroups();
    if (data?.status === 'fail') {
        throw new Error(data?.data || data?.message || 'Failed to list groups');
    }
    return Array.isArray(data?.groups) ? data.groups : [];
}

// create/rename/delete each echo back the authoritative post-change `groups` list, so
// callers can update the dropdown from the response rather than re-fetching via the
// list endpoint (which is unavailable for vendor accounts). Returns null when the
// response omitted the list, signalling the caller to fall back to a refresh.
const groupsFrom = (data: any) => (Array.isArray(data?.groups) ? data.groups : null);

// The API rejects a name that already exists (case-insensitive) with
// { status: 'fail', data: '...already exists' }. Detect that so callers can show a
// name-specific message instead of a generic "couldn't create/rename" one.
const isDuplicateName = (data: any) => /already exists/i.test(String(data?.data ?? data?.message ?? ''));

// Resolves { group, groups, duplicate }: group is null when the name was rejected;
// duplicate is true specifically for a name clash (vs. any other soft failure).
export async function createGroup(name: string) {
    const data = await createRealtimeGroup(String(name).trim());
    if (data?.status === 'fail') return { group: null, groups: null, duplicate: isDuplicateName(data) };
    return { group: data?.group ?? null, groups: groupsFrom(data), duplicate: false };
}

export async function renameGroup(id: string, name: string) {
    const data = await updateRealtimeGroup(id, String(name).trim());
    const ok = data?.status !== 'fail';
    return { ok, groups: groupsFrom(data), duplicate: !ok && isDuplicateName(data) };
}

export async function deleteGroup(id: string) {
    // Deleting a group removes only the group; the API also strips its id from every
    // member device's group_ids, leaving the devices untouched.
    const data = await deleteRealtimeGroup(id);
    return { groups: groupsFrom(data) };
}

// Apply the manage-devices dialog as a single add/remove delta.
export async function updateGroupDevices(id: string, { add = [], remove = [] }: { add?: string[]; remove?: string[] }) {
    const data = await updateRealtimeGroupDevices(id, add, remove);
    return Array.isArray(data?.devices) ? data.devices : [];
}
